import React, { useMemo } from "react";
import { Box } from "@mui/material";
import {
  type GridEventListener,
  type GridRowHeightParams,
  type GridRowId,
} from "@mui/x-data-grid-pro";
import { AcquireBookDataGrid } from "@/components/DataGrid/AcquireBookDataGrid";
import { type BulkPropertyRow } from "../types";
import { type AcquirePolicy } from "../utils/acquirePolicy";
import {
  useCheckedVersion,
  type CheckedIdsStore,
} from "../hooks/useCheckedIdsStore";
import { BulkCheckboxCellProvider } from "./BulkCheckboxCell";
import { SOURCE_LINE_HEIGHT } from "./BulkRowSourceCell";
import { CHECKBOX_FIELD_NAMES } from "../utils/checkboxFields";
import { countSourceLines } from "../utils/rowSourceSummary";
import { BulkGridFooter, type BulkGridFooterProps } from "./BulkGridFooter";
import {
  BULK_COLUMNS,
  BULK_COLUMN_GROUPING_MODEL,
  BULK_MARK_SX,
} from "./bulkColumns";

// stickyHeader の下端版。ページをスクロールしてもグリッドのフッター（件数・内訳・ページ送り）が
// 画面下端に残る。画面固定（position: fixed）ではないためカード末尾では通常のブロックとして収まる
const STICKY_FOOTER_SX = {
  // MUI はフッターをフッターと同じ高さの無名要素で包むため、そのままだと貼り付き（position: sticky）の
  // 可動範囲がその要素に閉じて動かない。包みを畳んでグリッド本体を可動範囲にする
  "& > div:has(> .MuiDataGrid-footerContainer)": { display: "contents" },
  "& .MuiDataGrid-footerContainer": {
    position: "sticky",
    bottom: 0,
    zIndex: 1,
    bgcolor: "background.paper",
  },
} as const;

// レンダリングごとの新規オブジェクト生成で DataGrid 側の再描画を誘発しないよう、
// 値が変わらないスタイル指定・components プロパティはモジュールスコープに固定する
const GRID_SX = { ...BULK_MARK_SX, ...STICKY_FOOTER_SX };
const GRID_COMPONENTS = { Footer: BulkGridFooter };

// density="standard" の既定行高。取得方法セルが2行以上のときだけ1行分ずつ伸ばす
const BASE_ROW_HEIGHT = 52;

export interface BulkAcquisitionGridProps {
  store: CheckedIdsStore;
  /** 依頼対象になる全行（絞り込みで非表示の行も内訳に含める） */
  rows: BulkPropertyRow[];
  /** 表示中の行 */
  filteredRows: BulkPropertyRow[];
  /** 表示中の行ID。null は全行表示 */
  visibleRowIds: Set<GridRowId> | null;
  policy: AcquirePolicy;
  pastDays: number;
  excludeAcquired: boolean;
  onToggle: (field: string, row: BulkPropertyRow) => void;
  onColumnHeaderDoubleClick: GridEventListener<"columnHeaderDoubleClick">;
}

/**
 * チェック列付きの一覧グリッド。チェック状態・取得条件はセルとフッターが context 経由で購読し、
 * 列定義は固定のまま可視セルだけが再評価される。
 * props が変わらない親の再レンダリング（経過日数の打鍵など）ではグリッドを再描画しない。
 * チェック操作では行高の再計算のために再レンダリングするが、列定義は同じ参照のまま渡す
 */
export const BulkAcquisitionGrid: React.FC<BulkAcquisitionGridProps> =
  React.memo(
    ({
      store,
      rows,
      filteredRows,
      visibleRowIds,
      policy,
      pastDays,
      excludeAcquired,
      onToggle,
      onColumnHeaderDoubleClick,
    }) => {
      // 基準時刻はセルごとに生成すると経過日数の境界で判定がセル間でずれうるため、条件と一緒に1つ配る
      const cellContext = useMemo(
        () => ({
          store,
          policy,
          pastDays,
          excludeAcquired,
          now: new Date(),
          onToggle,
        }),
        [store, policy, pastDays, excludeAcquired, onToggle]
      );

      // フッターに件数・内訳とページ送りをまとめる。チェック状態の購読はフッター内に閉じる
      const footerProps = useMemo(
        (): BulkGridFooterProps => ({
          store,
          rows,
          visibleRowIds,
          policy,
          pastDays,
        }),
        [store, rows, visibleRowIds, policy, pastDays]
      );
      const gridComponentsProps = useMemo(
        () => ({ footer: footerProps }),
        [footerProps]
      );

      // 行高は取得方法セルの行数から数値で決める（実測の "auto" は5万行では重い）。
      // チェック状態はグリッドの外にあるため、版数が変わるたびに関数を作り直して DataGrid に
      // 再計算させる。resetRowHeights は内部の高さ表を消してから計算し直すためスクロール位置が動く
      const checkedVersion = useCheckedVersion(store);
      const getRowHeight = useMemo(() => {
        void checkedVersion;
        return ({ id, model }: GridRowHeightParams): number =>
          BASE_ROW_HEIGHT +
          SOURCE_LINE_HEIGHT *
            (countSourceLines(
              model as BulkPropertyRow,
              store.getRowFields(CHECKBOX_FIELD_NAMES, id),
              policy,
              pastDays,
              cellContext.now
            ) -
              1);
      }, [store, policy, pastDays, cellContext.now, checkedVersion]);

      return (
        // 高さ固定＋内部スクロールにすることで、行数が多くてもページ全体が縦に伸びない
        <Box sx={{ height: 600 }}>
          <BulkCheckboxCellProvider value={cellContext}>
            <AcquireBookDataGrid
              getRowHeight={getRowHeight}
              // ページを下までスクロールしたとき、列見出しがアプリヘッダーに隠れないようにする
              stickyHeader
              rows={filteredRows}
              columns={BULK_COLUMNS}
              sx={GRID_SX}
              components={GRID_COMPONENTS}
              componentsProps={gridComponentsProps}
              experimentalFeatures={{ columnGrouping: true }}
              columnGroupingModel={BULK_COLUMN_GROUPING_MODEL}
              onColumnHeaderDoubleClick={onColumnHeaderDoubleClick}
              disableColumnMenu
              disableSelectionOnClick
            />
          </BulkCheckboxCellProvider>
        </Box>
      );
    }
  );
BulkAcquisitionGrid.displayName = "BulkAcquisitionGrid";
